/**
 * `alertWordsFor`, bound to the signed-in reader and the active site's
 * clock, for the toast stack and the bell.
 */
import { useCallback, useMemo } from 'react';
import type { AlertViewModel } from '../../api/adapters';
import { audienceFor } from '../../auth/roles';
import { useAuth } from '../../auth/useAuth';
import { resolveSiteTimeZone } from '../../screens/Tasks/site-timezone';
import { useActiveSite } from '../useActiveSite';
import { alertWordsFor, type AlertWords } from './alert-words';

export function useAlertWords(): (alert: AlertViewModel) => AlertWords {
  const { roles } = useAuth();
  const { site, sites } = useActiveSite();
  // `audienceFor`, never a role list of our own: a mixed account reads
  // ops, and `service_operator` alone reads site (amendment A2).
  const audience = audienceFor(roles);
  const siteId = site?.id ?? null;
  const clock = useMemo(() => resolveSiteTimeZone(sites, siteId), [sites, siteId]);
  // The wall clock is read per call, only to name a time's day: a bell
  // re-rendered after midnight says "yesterday 23:58", not "23:58".
  return useCallback(
    (alert: AlertViewModel) => alertWordsFor(alert, audience, clock, Date.now()),
    [audience, clock],
  );
}
